/**
 * SeaTrader — Application entry point (Phase 6: thin client)
 *
 * Boot sequence:
 *   1. Load port data + sea routes (static JSON — still fetched client-side)
 *   2. Init Leaflet map + layers
 *   3. Init UI (HUD, UIManager, panels, notifications) — all hidden behind auth overlay
 *   4. Auth check: if token exists and is valid → skip auth screen
 *   5. Auth screen (AuthModal) if no valid token
 *   6. Connect WebSocket → onSnapshot → hydrateFromSnapshot → showGameUI
 *   7. Delta loop: onDelta → GameState.applyDelta → EventBus → re-render
 *   8. Reconnect handling → HUD live indicator
 *
 * What is NOT here (moved to server):
 *   - System imports (NavigationSystem, EconomySystem, TradeSystem, etc.)
 *   - GameEngine tick loop
 *   - SaveManager / autosave
 *   - initNewGame / loadGame
 */

import 'leaflet/dist/leaflet.css';

import { EventBus }   from './core/EventBus.js';
import { GameState }  from './core/GameState.js';
import { GameEngine } from './core/GameEngine.js';  // stub — imported for compat

import { Port }       from './entities/Port.js';

import { MapManager }    from './map/MapManager.js';
import { MapLayers }     from './map/MapLayers.js';
import { PortMarker }    from './map/PortMarker.js';
import { ShipMarker }    from './map/ShipMarker.js';
import { OtherShipMarker } from './map/OtherShipMarker.js';
import { ShipFilterControl } from './map/ShipFilterControl.js';
import { RouteRenderer } from './map/RouteRenderer.js';
import { initRouteStyleSync } from './map/routeStyleSync.js';

import { HUD }               from './ui/HUD.js';
import { UIManager }         from './ui/UIManager.js';
import { NotificationToast } from './ui/components/NotificationToast.js';
import { ShipyardPanel }     from './ui/panels/ShipyardPanel.js';
import { AdminPanel }        from './ui/panels/AdminPanel.js';
import { AdminEventsPanel }  from './ui/modals/AdminEventsPanel.js';
import { AdminPlayersPanel } from './ui/modals/AdminPlayersPanel.js';
import { DevToolsPanel }     from './ui/modals/DevToolsPanel.js';
import { AuthModal }         from './ui/modals/AuthModal.js';

import { AnalyticsCollector } from './systems/AnalyticsCollector.js';

import { initSeaRoutes } from './utils/seaRoutes.js';
import { interpolatePolyline, bearing } from './utils/math.js';

import {
  getToken, saveToken, apiMe, apiOidcExchange,
  connect, disconnect, isConnected,
  onSnapshot, onDelta, onDisconnect,
  setIsAdmin, clearToken
} from './core/ServerConnection.js';

// ---------- globals ----------
let mapLayers, portMarker, shipMarker, otherShipMarker, shipFilterControl, routeRenderer;
let hud, uiManager, notifications;
let _portsRaw = null;

// ================================================
// Boot
// ================================================
async function boot() {
  console.log('[SeaTrader] Booting…');

  // ── 1. Map ──────────────────────────────────────────────
  const mapManager = new MapManager();
  const map        = mapManager.init();
  mapLayers        = new MapLayers(map);
  portMarker       = new PortMarker(mapLayers);
  shipMarker       = new ShipMarker(mapLayers);
  otherShipMarker  = new OtherShipMarker(mapLayers);
  routeRenderer    = new RouteRenderer(mapLayers, { getStyleContext: () => ({
    now: GameState.currentDate, contracts: GameState.contracts.active, tradeRoutes: GameState.tradeRoutes }) });
  initRouteStyleSync(routeRenderer);

  shipFilterControl = new ShipFilterControl();
  shipFilterControl.addTo(map);

  // ── 2. Port / sea-route data ─────────────────────────────
  const portsData      = await fetchJson('/data/ports.json');
  _portsRaw = portsData.ports;
  for (const pData of _portsRaw) {
    const port = new Port(pData);
    GameState.ports.set(port.id, port);
    portMarker.addPort(port);
  }

  const seaRoutesData = await fetchJson('/data/seaWaypoints.json');
  initSeaRoutes(seaRoutesData);

  // ── 3. UI (hidden behind auth screen until game:loaded) ──
  notifications = new NotificationToast();
  notifications.init();

  // GameEngine stub — passed for compat; methods are no-ops
  const engine = new GameEngine();
  hud = new HUD(engine);

  // UIManager receives null stubs for systems — in Phase 6 panels call
  // serverIntent() directly instead of going through system objects.
  uiManager = new UIManager({
    navigationSystem: _makeIntentStub(),
    tradeSystem:      _makeIntentStub(),
    fuelSystem:       _makeIntentStub(),
    contractSystem:   _makeIntentStub(),
    researchSystem:   _makeIntentStub(),
    warehouseSystem:  _makeIntentStub(),
    staffSystem:      _makeIntentStub(),
    saveManager:      null
  });
  uiManager.init();

  // Shipyard modal (listens on EventBus)
  new ShipyardPanel();

  // Analytics (passive, read-only)
  const analytics = new AnalyticsCollector();
  analytics.init();

  // Admin panel for route waypoint editing — dev-only (GameState.devControlsEnabled,
  // same GAME_ENV=dev gate as the rest of DevToolsPanel.js). Was previously
  // reachable via Ctrl+Shift+A in EVERY environment with no gate at all —
  // worse, its save endpoint (server/index.js's /api/save-waypoints) had no
  // auth or environment check either, so anyone who knew the shortcut (or
  // just POSTed the endpoint directly) could overwrite the live sea-route
  // network in test or prod. Both are now dev-only; see server/index.js's
  // isDevEnvironment() check on the endpoint itself, which is the real
  // boundary — this client-side gate is just what stops the shortcut from
  // offering it in the first place.
  const adminPanel = new AdminPanel(mapLayers);
  const toggleAdminPanel = () => {
    if (!GameState.devControlsEnabled) return;
    adminPanel.toggle();
  };
  document.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'A') {
      e.preventDefault();
      toggleAdminPanel();
    }
  });
  // DevToolsPanel.js's "Waypoint Editor" button — opens via the same gated
  // path as the keyboard shortcut.
  EventBus.on('ui:adminPanelOpen', toggleAdminPanel);

  // Admin: real-world event injection (Phase 9) — instantiated unconditionally
  // (cheap, event-driven, does nothing until opened) but its HUD entry point
  // is only revealed for admin accounts once auth resolves (see below).
  new AdminEventsPanel();
  new AdminPlayersPanel();

  // Dev tools (add money / teleport ship / hire any crew tier) — same
  // "instantiate unconditionally, HUD button reveals it" pattern as
  // AdminEventsPanel above, but gated on GameState.devControlsEnabled
  // (HUD.js's _updateDevControls(), driven by the WS snapshot) rather than
  // isAdmin — see server/transport/intents/dev.js.
  new DevToolsPanel();

  // ── 4. Map event wiring (same as before — map renders from GameState) ──

  EventBus.on('ship:arrived',  () => syncMapMarkers());
  EventBus.on('ship:departed', () => syncMapMarkers());
  EventBus.on('ship:purchased',() => syncMapMarkers());
  // Held departure queued / redirected / cancelled -> planned-route line
  // (local emit carries { ship }, server delta carries { shipId })
  EventBus.on('ship:handlingChanged', (e) => {
    const ship = e?.ship || GameState.getShip(e?.shipId);
    if (ship) routeRenderer.upsert(ship);
  });
  EventBus.on('ship:sold',     () => syncMapMarkers());
  EventBus.on('ship:renamed',  () => syncMapMarkers());
  EventBus.on('game:loaded',   () => syncMapMarkers());
  // Other players' ships (periodic world:ships broadcast — see
  // server/transport/websocket.js) and the filter-control checkboxes both
  // just need the 'otherShips' layer resynced, not the full syncMapMarkers().
  EventBus.on('world:shipsUpdated',    () => syncOtherShipMarkers());
  EventBus.on('map:shipFiltersChanged',() => syncOtherShipMarkers());
  // Re-stroke this player's own fleet with their newly chosen colour
  // (src/ui/modals/GameMenuModal.js) — ShipMarker.upsert() reads
  // GameState.player.companyColor fresh each call, so a full resync picks
  // it up immediately rather than waiting for the next unrelated ship event.
  EventBus.on('player:companyColorChanged', () => syncMapMarkers());

  EventBus.on('ui:shipSelected', ({ ship }) => {
    if (ship?.position) {
      mapLayers.map.setView([ship.position.lat, ship.position.lng], 6);
    }
  });

  EventBus.on('ui:portSelected', ({ port }) => {
    if (port?.position) {
      const bounds = mapLayers.map.getBounds();
      if (!bounds.contains([port.position.lat, port.position.lng])) {
        mapLayers.map.setView([port.position.lat, port.position.lng], 6);
      }
      portMarker.flash(port.id);
    }
  });

  EventBus.on('map:flashPort',   ({ portId }) => portMarker.flash(portId));
  EventBus.on('map:dimPorts',    ({ shipType }) => portMarker.dimForShip(shipType));
  EventBus.on('map:resetDim',    () => portMarker.resetDim());
  EventBus.on('map:syncRequested', () => syncMapMarkers());

  // ── 5. WebSocket handlers ────────────────────────────────

  onSnapshot(async (snapshot) => {
    console.log('[Boot] Snapshot received — hydrating GameState…');
    try {
      await GameState.hydrateFromSnapshot(snapshot);
    } catch (err) {
      console.error('[Boot] hydrateFromSnapshot failed:', err);
    }
    // game:loaded is emitted inside hydrateFromSnapshot; HUD.refresh()
    // is called from there via event. Also show the game UI.
    showGameUI();
    hud.setLiveState('live');
    notifications.show('Connected to SeaTrader world', 'success');

    // Pan to first ship
    const firstShip = GameState.getAllShips()[0];
    if (firstShip?.position) {
      mapLayers.map.setView([firstShip.position.lat, firstShip.position.lng], 5);
    }
    setTimeout(() => map.invalidateSize(), 200);
  });

  onDelta(async (delta) => {
    try {
      await GameState.applyDelta(delta);
    } catch (err) {
      console.error('[Boot] applyDelta failed for', delta.type, err);
    }
    // Ship marker sync for movement deltas
    if (delta.type === 'ship:arrived' || delta.type === 'ship:departed') {
      syncMapMarkers();
    }
  });

  // Runs continuously regardless of connection state (harmless no-op while
  // there are no sailing ships) — see _tickShipInterpolation()'s docblock.
  setInterval(_tickShipInterpolation, 500);
  // Same idea, for the HUD clock — see _tickClock()'s docblock.
  setInterval(_tickClock, 500);

  onDisconnect(({ code, reason }) => {
    console.warn(`[Boot] WS disconnected: ${code} ${reason}`);
    if (reason === 'world:notReady') {
      hud.setLiveState('reconnecting');
      notifications.show('Server starting — reconnecting…', 'warning');
    } else if (code === 4003) {
      hud.setLiveState('offline');
      clearToken();
      notifications.show('Your account has been removed by an administrator.', 'error');
    } else if (code === 4000) {
      // Not reconnected automatically (see ServerConnection) — reload to rejoin
      hud.setLiveState('offline');
      notifications.show('Game open in too many tabs — this one was disconnected. Reload to reconnect.', 'warning');
    } else {
      hud.setLiveState('reconnecting');
    }
    // ServerConnection handles the reconnect backoff automatically.
    // When reconnect succeeds, onSnapshot fires again and we re-hydrate.
  });

  // ── 6. Auth ─────────────────────────────────────────────

  const proceedAsAuthenticated = (isAdminAccount) => {
    setIsAdmin(isAdminAccount);
    hud.setAdminEventsVisible(!!isAdminAccount);
    connect();
  };

  // Returning from a Keycloak/Google login redirect: the callback (server/
  // auth/oidc.js) never puts the real session token in the URL — it lands
  // us back here with a short-lived, single-use `oidc_exchange` code, which
  // we trade for the real token via one POST. Must run BEFORE the existing-
  // token check below so a fresh Google sign-in takes priority over
  // whatever (if anything) was already in localStorage.
  const oidcExchangeCode = new URLSearchParams(location.search).get('oidc_exchange');
  if (oidcExchangeCode) {
    // Scrub the code from the URL immediately — it's single-use and we
    // don't want it lingering in browser history/reload behavior either
    // way (success or failure).
    history.replaceState(null, '', location.pathname);

    const result = await apiOidcExchange(oidcExchangeCode);
    if (result.success) {
      console.log('[Boot] Google sign-in complete, connecting as', result.username);
      saveToken(result.token);
      proceedAsAuthenticated(!!result.isAdmin);
      return;
    }
    console.warn('[Boot] OIDC exchange failed:', result.error);
    // Fall through to the normal auth screen below with the error surfaced.
  }

  const existingToken = getToken();
  if (existingToken) {
    const me = await apiMe();
    if (me && me.accountId) {
      // Valid token — connect directly
      console.log('[Boot] Token valid, connecting as', me.username);
      proceedAsAuthenticated(me.isAdmin);
      return;
    }
    // Token invalid / expired — fall through to auth screen
  }

  // Show auth screen
  const authModal = new AuthModal((token, isAdminAccount) => {
    authModal.hide();
    proceedAsAuthenticated(isAdminAccount);
  });
  authModal.show(oidcExchangeCode ? 'Google sign-in failed or expired. Please try again.' : null);

  console.log('[SeaTrader] Boot complete.');
}

// ================================================
// Helpers
// ================================================

/**
 * Fetch boot data, retrying transient network failures (a dropped
 * connection, flaky Wi-Fi) before giving up — a single failed request here
 * otherwise kills the whole boot with "TypeError: Failed to fetch".
 */
async function fetchJson(url, attempts = 3) {
  for (let i = 1; ; i++) {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      if (i >= attempts) throw new Error(`Could not load ${url} (${err.message})`);
      console.warn(`[Boot] ${url} failed (attempt ${i}/${attempts}):`, err);
      await new Promise(r => setTimeout(r, 500 * i));
    }
  }
}

/** Show the main game UI (hidden by default behind the auth overlay) */
function showGameUI() {
  // The map + panels are always in the DOM; nothing special to unhide since
  // AuthModal covers them. Once AuthModal.hide() is called by the auth flow,
  // the game is visible. But in case game was shown before auth finished,
  // just trigger a tab re-render.
  uiManager.showTab('fleet');
}

/**
 * Client-side ship position interpolation (Phase 6 gap fix).
 *
 * The server only pushes a ship's position at two discrete moments —
 * ship:departed (once, at the start of a voyage) and ship:arrived (once,
 * at the end) — nothing pushes intermediate positions while a ship is
 * sailing. Without this, a ship would appear frozen at its departure port
 * until the next full snapshot (e.g. a page refresh) revealed wherever the
 * server had actually simulated it to by then.
 *
 * This extrapolates position locally between those two deltas using the
 * real route (waypoints/distance, from ship:departed — see GameState.js's
 * applyDelta), the ship's own speed, and the server's timeScale (from the
 * WS snapshot). The server remains fully authoritative: this is a visual
 * approximation only, corrected the moment the real ship:arrived delta
 * lands, so any client-side drift never affects actual game state.
 *
 * Deliberately accumulates distance PER TICK (ship._clientDistanceTraveledNm
 * += this tick's delta) rather than computing elapsed-since-departure from a
 * single fixed timestamp. The latter (an earlier version of this function)
 * silently assumed GameState.timeScale had been constant for the ship's
 * entire voyage — correct until the dev speed slider (server/transport/
 * intents/dev.js) made it possible to change timeScale mid-voyage, at which
 * point recomputing "distance so far" from scratch using only the NEW
 * timeScale applied to the FULL elapsed real time since departure produced
 * wildly wrong results (e.g. jumping backward toward the origin after
 * switching from a high speed to a low one). Accumulating per-tick avoids
 * this entirely: each small increment only ever uses whatever timeScale is
 * current *at that moment*, so a mid-voyage change is correct by
 * construction and pausing is just "add zero this tick" — no retroactive
 * correction needed anywhere (see GameState.js's dev:controlsChanged case).
 */
function _tickShipInterpolation() {
  const now = Date.now();
  let anyMoved = false;
  for (const ship of GameState.getAllShips()) {
    if (ship.status !== 'sailing') continue;
    const route = ship.currentRoute;
    if (!route?.waypoints || route.waypoints.length < 2 || !route.distance) continue;

    if (ship._clientLastTickMs == null) {
      // First time this ship has been interpolated client-side — either it
      // just departed (GameState.js's ship:departed case seeds
      // voyageProgress at 0 and clears this) or we're picking it up
      // mid-voyage after a fresh connect/reconnect (voyageProgress here is
      // the server's last-known value from the snapshot). Either way,
      // start accumulating from exactly there rather than assuming 0.
      ship._clientDistanceTraveledNm = (ship.voyageProgress || 0) * route.distance;
      ship._clientLastTickMs = now;
      continue; // nothing elapsed yet to apply this pass
    }

    const deltaRealMs = now - ship._clientLastTickMs;
    ship._clientLastTickMs = now;

    // Dev-only pause (server/transport/intents/dev.js) freezes the real
    // server-side tick loop; skip accumulating distance this tick to match
    // (but still advance _clientLastTickMs above, so the paused duration
    // itself doesn't get counted once resumed).
    if (!GameState.paused) {
      const deltaGameSec = (deltaRealMs / 1000) * (GameState.timeScale || 1);
      ship._clientDistanceTraveledNm += ship.getCurrentSpeed() * deltaGameSec / 3600; // knots = nm/hr
    }

    // Cap just short of 1 — actual arrival is signaled by the server's own
    // ship:arrived delta, not by this client-side estimate reaching 100%;
    // capping avoids a jarring "arrived early" visual snap-back if the
    // client's estimate slightly outruns the server's real tick timing.
    const progress = Math.max(0, Math.min(0.99, ship._clientDistanceTraveledNm / route.distance));
    ship.voyageProgress = progress;
    ship.position = interpolatePolyline(route.waypoints, progress);

    // Point the map arrow the way the ship is actually heading — sample a
    // point a little further along the same polyline and take the bearing
    // to it, rather than tracking a server-pushed heading (nothing pushes
    // intermediate heading updates any more than it pushes intermediate
    // positions — see this function's docblock).
    const aheadPosition = interpolatePolyline(route.waypoints, Math.min(1, progress + 0.001));
    if (aheadPosition.lat !== ship.position.lat || aheadPosition.lng !== ship.position.lng) {
      ship.heading = bearing(ship.position, aheadPosition);
    }
    anyMoved = true;
  }
  if (anyMoved) syncMapMarkers();
}

/**
 * Locally extrapolate GameState.currentDate forward between real server
 * pushes, the same idea as _tickShipInterpolation() above but for the HUD
 * clock. The server only pushes a fresh date on an actual day boundary
 * (time:dayPassed) or a full snapshot (reconnect) — nothing pushes
 * intermediate updates — so without this, the displayed date/time sits
 * frozen at whatever it was last set to and can silently fall arbitrarily
 * far behind reality on a long-lived tab (found via a live player report:
 * the world clock itself was correct, but a tab open for hours between day
 * boundaries kept showing a stale time from hours earlier).
 *
 * Mutates GameState.currentDate itself (not a separate display-only
 * variable) — this also incidentally sharpens anything else that reads it
 * client-side (e.g. ResearchPanel's permit-cooldown "days remaining" calc).
 * Safe to do since any real delta (time:dayPassed, or a fresh snapshot)
 * always overwrites it outright — this is purely a between-updates
 * approximation, corrected the moment real data arrives, exactly like the
 * ship-position interpolation it's modeled on.
 */
let _lastClockTickMs = null;
function _tickClock() {
  const now = Date.now();
  if (_lastClockTickMs == null) { _lastClockTickMs = now; return; }
  const deltaRealMs = now - _lastClockTickMs;
  _lastClockTickMs = now;

  if (GameState.paused) return; // matches _tickShipInterpolation()'s pause handling
  if (!(GameState.currentDate instanceof Date)) return; // not hydrated yet

  const deltaGameMs = deltaRealMs * (GameState.timeScale || 1);
  GameState.currentDate = new Date(GameState.currentDate.getTime() + deltaGameMs);
  EventBus.emit('time:tick');
}

/** Sync ship markers and route lines from GameState */
function syncMapMarkers() {
  for (const ship of GameState.getAllShips()) {
    shipMarker.upsert(ship);
    routeRenderer.upsert(ship);
  }
  if (shipMarker.markers) {
    for (const id of shipMarker.markers.keys()) {
      if (!GameState.ships.has(id)) shipMarker.remove(id);
    }
  }
  if (routeRenderer.lines) {
    for (const id of routeRenderer.lines.keys()) {
      if (!GameState.ships.has(id) || !GameState.getShip(id)?.currentRoute) {
        routeRenderer.remove(id);
      }
    }
  }
  routeRenderer.prunePlanned((id) => GameState.ships.has(id));
  syncOtherShipMarkers();
}

/** Sync the vesselfinder-style "other players' ships" layer from GameState */
function syncOtherShipMarkers() {
  otherShipMarker.sync(
    GameState.getAllOtherShips(),
    (ship) => shipFilterControl.isVisible(ship)
  );
}

/**
 * Create a stub object for system parameters.
 * In Phase 6 panels call serverIntent() directly; the UIManager passes
 * these stubs through but panels should not call them. If a panel does call
 * a stub method, it throws a clear error instead of failing silently.
 */
function _makeIntentStub() {
  return new Proxy({}, {
    get(target, prop) {
      return () => {
        console.warn(`[Phase6] System stub called: ${String(prop)} — wire to serverIntent() instead`);
        return { success: false, error: 'Not available client-side in Phase 6' };
      };
    }
  });
}

// ================================================
// Debug
// ================================================
window.GameState = GameState;
window.EventBus  = EventBus;

// ================================================
// Go
// ================================================
boot().catch(err => {
  console.error('[SeaTrader] Boot failed:', err);
  document.body.innerHTML = `<div style="color:#e74c3c;padding:40px;font-family:sans-serif">
    <h2>SeaTrader — Boot Error</h2><pre>${err.stack || err}</pre>
    <button onclick="location.reload()" style="margin-top:16px;padding:8px 16px;cursor:pointer">Reload</button>
  </div>`;
});
